import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Dialog, Box, Grid } from "@mui/material";
import cn from "classnames";
import { observer } from "mobx-react";
import { Button } from "src/shared/ui/Button";
import useStores from "src/hooks/useStores";
import { useLocale } from "src/providers/LocaleProvider";
import { GameModeCreate, ICreateGameRequestNew } from "src/service/api/lobby/requestResponses";
import { errorService } from "src/service/services";
import useWindowSize from "src/hooks/useWindowSize";
import { useHistory } from "react-router-dom";
import paths from "src/consts/paths";
import { GameTimeValues, GameTypeNew } from "../../../../store/lobbyStore";
import { GameCreateFen, UserBalanceType } from "../../../../store/models";
import { bets, freeBets, matches as matchCounts } from "src/store/gameStore/constants/bets";
import { useStyles } from "./styles";
import createGameIcon from "src/assets/icons/Plus.svg";
import closeIcon from "src/assets/icons/close.svg";
import time2mIcon from "src/assets/icons/ultraBulllet.svg";
import time3mIcon from "src/assets/icons/Bullet.svg";
import time5mIcon from "src/assets/icons/flash.svg";
import time10mIcon from "src/assets/icons/rabbit.svg";
import time20mIcon from "src/assets/icons/snail.svg";
import coinIcon from "src/assets/icons/coin.svg";
import flagIcon from "src/assets/icons/matchIcon.svg";
import arrowIcon from "src/assets/icons/lightArrow.svg";
import silverCoinIcon from "src/assets/icons/freeCoin.svg";
interface ICreateGameModalProps {
    open: boolean;
    onClose: () => void;
}

const CreateGameModal = observer(({ open, onClose }: ICreateGameModalProps) => {
    const { lobbyStore, authStore } = useStores();
    const history = useHistory();
    const { width } = useWindowSize();
    const isAuthorized = authStore.isAuthorized;
    const bonus = authStore?.currentUser?.balance.bonus;
    const play = authStore?.currentUser?.balance.play;
    const free = authStore?.currentUser?.balance.coins;
    const isFreePlay = document.location.href.indexOf("lobby-free-play") > 0;
    const betList = isFreePlay ? freeBets : bets;

    const defaultValues = useMemo(() => ({
        bet: betList[0],
        time: GameTimeValues["2m"],
        matches: 1,
        type: GameTypeNew.classic,
        gameMode: 1,
        showMatchSelector: false,
        showAllBetsMobile: false,
    }), [betList]);

    const [bet, setBet] = useState(defaultValues.bet);
    const [time, setTime] = useState(defaultValues.time);
    const [matches, setMatches] = useState(defaultValues.matches);
    const [type, setType] = useState(defaultValues.type);
    const [gameMode, setGameMode] = useState(defaultValues.gameMode);
    const [showMatchSelector, setShowMatchSelector] = useState(defaultValues.showMatchSelector);
    const [showAllBetsMobile, setShowAllBetsMobile] = useState(defaultValues.showAllBetsMobile);

    const classes = useStyles();
    const {
        lobby: {
            createGame: {
                buttons: {
                    create,
                    createTitle,
                    min,
                    potLimit,
                    classic,
                    match,
                    betTitle,
                    timeTitle,
                    typeTitle,
                    modeTitle,
                    matchTitle
                },
            },
        },
    } = useLocale();

    const isMobile = width <= 600;

    const resetToDefaults = useCallback(() => {
        setBet(defaultValues.bet);
        setTime(defaultValues.time);
        setMatches(defaultValues.matches);
        setType(defaultValues.type);
        setGameMode(defaultValues.gameMode);
        setShowMatchSelector(defaultValues.showMatchSelector);
        setShowAllBetsMobile(defaultValues.showAllBetsMobile);
    }, [defaultValues]);

    const handleCreateGame = useCallback(async () => {
        if (!isAuthorized) {
            return;
        }
        if (
            isAuthorized &&
            ((!play && !isFreePlay && !bonus) ||
                (isFreePlay && !free))
        ) {
            errorService.sendError("Недостаточно средств для создания игры");
            setTimeout(() => {
                history.push(paths.cashbox);
            }, 1200);
            return;
        }

        const baseGameParams: ICreateGameRequestNew = {
            bank: bet,
            fenType: type === GameTypeNew.classic ? GameCreateFen.classic : GameCreateFen.nineHundredSixty,
            freeGame: lobbyStore.lobbyMode === UserBalanceType.play ? 1 : 2,
            gameType: isFreePlay ? GameModeCreate.Free : gameMode,
            gameCount: gameMode === 2 ? matches : 1,
            time: time,
            sendTo: lobbyStore.selectedFriend ?? undefined,
        };

        try {
            await lobbyStore.createGameNew(baseGameParams);

            resetToDefaults();

            onClose();
        } catch (e: any) {
            errorService.sendError(e?.response?.data?.message || "Ошибка создания игры");
        }
    }, [bet, time, matches, type, isAuthorized, play, isFreePlay, bonus, free, resetToDefaults, onClose]);

    const times = useMemo(() => [
        { value: GameTimeValues["2m"], label: `2 ${min('min')}`, icon: time2mIcon },
        { value: GameTimeValues["3m"], label: `3 ${min('min')}`, icon: time3mIcon },
        { value: GameTimeValues["5m"], label: `5 ${min('min')}`, icon: time5mIcon },
        { value: GameTimeValues["10m"], label: `10 ${min('min')}`, icon: time10mIcon },
        { value: GameTimeValues["20m"], label: `20 ${min('min')}`, icon: time20mIcon }
    ], [min]);

    const types = [
        { value: GameTypeNew.classic, label: "Classic" },
        { value: GameTypeNew.nineHundredSixty, label: "960" }
    ];

    const handleGameModeChange = (mode: number) => {
        setGameMode(mode);
        if (mode === 2) {
            setShowMatchSelector(true);
        } else {
            setShowMatchSelector(false);
        }
    };

    const handleClose = () => {
        resetToDefaults();

        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} className={classes.mainLobbyFilterContainer}>
            <div className={classes.modalContent}>
                <div className={classes.modalTitleBlock}>
                    <p className={classes.modalTitle}>
                        <img src={createGameIcon} className={classes.createIcon} alt="Filter" />
                        {createTitle('Создание игры')}
                    </p>
                    <img src={closeIcon} onClick={handleClose} className={classes.closeIcon} alt="Close" />
                </div>

                <div className={classes.filterSection}>
                    {/* Ставки */}
                    <div className={classes.sectionHeader}>
                        <img
                            src={isFreePlay ? silverCoinIcon : coinIcon}
                            className={classes.headerCoinIcon}
                            alt="coin"
                        />
                        {betTitle('Bet')}
                    </div>

                    <div className={classes.betSection}>
                        {betList
                            .filter(amount => isFreePlay || !isMobile || showAllBetsMobile || amount <= 200)
                            .map((amount, index) => (
                                <div
                                    key={amount}
                                    className={cn(classes.betItem, {
                                        [classes.betItemFull]: !isFreePlay && index === betList.length - 1,
                                        [classes.betItemHalf]: isFreePlay && index >= betList.length - 2,
                                        [classes.selected]: bet === amount,
                                    })}
                                    onClick={() => setBet(amount)}
                                >
                                    <div className={cn(classes.betItemContent, {
                                        [classes.centeredContent]: !isFreePlay && index === betList.length - 1,
                                    })}>
                                        <span className={bet === amount ? (isFreePlay ? classes.silverText : classes.goldText) : classes.grayText}>
                                            {amount}
                                        </span>
                                    </div>
                                </div>
                            ))}
                    </div>
                    {isMobile && !isFreePlay && (
                        <div className={classes.betToggleMobile} onClick={() => setShowAllBetsMobile(prev => !prev)}>
                            <img
                                src={arrowIcon}
                                alt="toggle"
                                className={cn(classes.arrowIcon, {
                                    [classes.arrowIconRotated]: showAllBetsMobile,
                                })}
                            />
                        </div>
                    )}

                    {/* Время */}
                    <div className={classes.sectionHeader}>
                        {timeTitle('Time')}
                    </div>

                    <div className={classes.timeSection}>
                        {times.map((timeItem) => (
                            <div
                                key={timeItem.value}
                                className={cn(classes.timeItem, {
                                    [classes.selected]: time === timeItem.value,
                                })}
                                onClick={() => setTime(timeItem.value)}
                            >
                                <img src={timeItem.icon} alt="" className={classes.timeIcon} />
                                <span>{timeItem.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Тип игры */}
                    <div className={classes.sectionHeader}>
                        {typeTitle('Type')}
                    </div>

                    <div className={classes.typeSection}>
                        {types.map((typeItem) => (
                            <div
                                key={typeItem.value}
                                className={cn(classes.typeItem, {
                                    [classes.selected]: type === typeItem.value,
                                })}
                                onClick={() => setType(typeItem.value)}
                            >
                                <span>{typeItem.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Режим игры */}
                    {!isFreePlay && (
                        <>
                            <div className={classes.sectionHeader}>
                                {modeTitle('Mode')}
                            </div>

                            <div className={classes.gameModeSection}>
                                <div
                                    className={cn(classes.gameModeItem, {
                                        [classes.selected]: gameMode === 1,
                                    })}
                                    onClick={() => handleGameModeChange(1)}
                                >
                                    {potLimit("Pot Limit")}
                                </div>
                                <div
                                    className={cn(classes.gameModeItem, {
                                        [classes.selected]: gameMode === 2,
                                    })}
                                    onClick={() => handleGameModeChange(2)}
                                >
                                    {match("Match")}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Количество матчей */}
                    {showMatchSelector && (
                        <>
                            <div className={classes.sectionHeader}>
                                {matchTitle('Match')}
                            </div>

                            <div className={cn(classes.matchSectionWrapper, {
                                [classes.matchSectionWrapperOpen]: showMatchSelector,
                            })}>
                                <div className={cn(classes.matchSection, classes.matchSectionAnimated)}>
                                    {matchCounts.map((mc, index) => (
                                        <div
                                            key={mc}
                                            className={cn(classes.matchItem, {
                                                [classes.selected]: matches === mc,
                                            })}
                                            onClick={() => setMatches(mc)}
                                            style={{ animationDelay: `${index * 80}ms` }}
                                        >
                                            <img src={flagIcon} alt="flag" style={{ width: 14, height: 14, marginRight: 6 }} />
                                            <span className={matches === mc ? classes.whiteText : classes.grayText}>
                                                {mc}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Кнопки */}
                <div className={classes.buttonBlock}>
                    <Button
                        text={create("Создать")}
                        variant="contained"
                        onClick={handleCreateGame}
                        className={`${classes.button} ${classes.applyButton}`}
                    />
                </div>
            </div>
        </Dialog>
    );
});

export default CreateGameModal;